import { Autocomplete, Stack, TextField } from '@mui/material';
import type { FieldProps, FormikHelpers } from 'formik';
import { Field, Form, Formik } from 'formik';
import { navigate } from 'gatsby';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import type { Flags, Schema } from 'yup';
import { array, boolean, object, string } from 'yup';

import RecordAttachmentForm from '@components/Attachments/RecordAttachmentForm';
import { FormikFhirDateField, FormikTextField } from '@components/FormikFields';
import { RecordSubmitButton } from '@components/RecordSubmitButton';
import RelatedRecordsForm from '@components/RelatedRecordsTable/RelatedRecordsForm';

import { VACCINE_CODE_OPTIONS } from '@constants/option-types/immunization';
import { IMMUNIZATIONS } from '@constants/routes';

import {
  useCreateImmunization,
  useUpdateImmunization,
} from '@hooks/api/immunization';
import useFhirRecordForm from '@hooks/useFhirRecordForm';
import { useFormat } from '@hooks/useFormat';
import { useIsMobile } from '@hooks/useIsMobile';
import { useSnackbar } from '@hooks/useSnackbar';

import { HorizontalRule } from '@layouts/AuthLayout';
import { ColumnLayout, DrilldownItem } from '@layouts/DrilldownStyle';

import { getImmunizationTemplate } from '@models/fhir-templates/immunization';
import type {
  FormValues,
  ImmunizationRecord,
  ImmunizationValues,
} from '@models/immunization';
import type { DrilldownProps } from '@models/record-drilldown';

import { getRecordSourceForSubmit } from '@utils/getRecordSourceForSubmit';
import { stripEmptyStrings } from '@utils/strip';

import type { LocationModelInput } from 'generated/types';

import DrilldownWrapper from '../Common/DrilldownWrapper';

const messages = defineMessages({
  title: {
    id: 'immunizationDrilldown.title',
    defaultMessage: 'Immunization',
  },
  vaccine: {
    id: 'immunizationDrilldown.vaccine',
    defaultMessage: 'Vaccine',
  },
  expirationDate: {
    id: 'immunizationDrilldown.expirationDate',
    defaultMessage: 'Expiration Date',
  },
  administrationDate: {
    id: 'immunizationDrilldown.administrationDate',
    defaultMessage: 'Administration Date',
  },
  facility: {
    id: 'GLOBAL.facility',
    defaultMessage: 'Facility',
  },
  practitioner: {
    id: 'GLOBAL.practitioner',
    defaultMessage: 'Practitioner',
  },
  city: {
    id: 'GLOBAL.city',
    defaultMessage: 'City',
  },
  region: {
    id: 'GLOBAL.region',
    defaultMessage: 'State/Province',
  },
  country: {
    id: 'GLOBAL.country',
    defaultMessage: 'Country',
  },
  dose: {
    id: 'immunizationDrilldown.dose',
    defaultMessage: 'Vaccine Dose',
  },
  brand: {
    id: 'immunizationDrilldown.brand',
    defaultMessage: 'Vaccine Brand',
  },
  route: {
    id: 'immunizationDrilldown.route',
    defaultMessage: 'Vaccine Route',
  },
  lotNumber: {
    id: 'immunizationDrilldown.lotNumber',
    defaultMessage: 'Vaccine Lot Number',
  },
  type: {
    id: 'immunizationDrilldown.type',
    defaultMessage: '{type}',
  },
  immunizationType: {
    id: 'immunizationDrilldown.immunizationType',
    defaultMessage: 'Immunization Type',
  },
  required: {
    id: 'GLOBAL.required',
    defaultMessage: 'This field is required',
  },
  error: {
    id: 'GLOBAL.error',
    defaultMessage: 'An error occurred. Please try again later.',
  },
});

interface ImmunizationProps extends DrilldownProps<ImmunizationRecord> {
  /**
   * When true, suppresses the default redirect to the IMMUNIZATIONS route
   * after successfully creating a new immunization.
   * - Default: false (keeps existing behavior in normal routes)
   * - In auto-curation flow, this should be set to true.
   */
  disableNavigateOnCreate?: boolean;
}

const ImmunizationDrilldown: React.FC<ImmunizationProps> = ({
  data,
  isCreating,
  isEditing,
  isCurator,
  loading: baseLoading,
  onSubmit,
  patientId,
  disableNavigateOnCreate = false,
}) => {
  const { formatMessage } = useIntl();
  const { formatDateLabel } = useFormat();
  const { enqueueSnackbar } = useSnackbar();
  const isMobile = useIsMobile('lg');
  const {
    formikHelpers: { baseFhirValidationSchema, initialFhirFormValues },
    loading: recordStatusAndSourceLoading,
    handleRecordFilesSourcePublishing,
  } = useFhirRecordForm();

  const patient_id = patientId;

  const [createImmunization, createImmunizationLoading] =
    useCreateImmunization();
  const [updateImmunization, updateImmunizationLoading] =
    useUpdateImmunization();

  const loading =
    baseLoading ||
    updateImmunizationLoading ||
    createImmunizationLoading ||
    recordStatusAndSourceLoading;

  const initialValues: FormValues = {
    ...initialFhirFormValues,
    title: data?.vaccineName ?? '',
    brand: data?.brand ?? '',
    city: data?.city ?? '',
    country: data?.country ?? '',
    dose: data?.dose ?? '',
    expirationDate: data?.expirationDate ?? '',
    facility: data?.facility ?? '',
    immunizationDate: data?.immunizationDate ?? '',
    lotNumber: data?.lotNumber ?? '',
    practitioner: data?.practitioner ?? '',
    region: data?.region ?? '',
    route: data?.route ?? '',
    selfReported: data?.selfReported ?? false,
    standard: data?.standard ?? true,
    associatedResources: [],
  };

  const immunValidationSchema: Schema<Partial<ImmunizationValues>> =
    object().shape({
      title: string().required(formatMessage(messages.required)),
      brand: string(),
      city: string(),
      country: string(),
      dose: string(),
      expirationDate: string(),
      facility: string(),
      immunizationDate: string().required(formatMessage(messages.required)),
      lotNumber: string(),
      practitioner: string(),
      region: string(),
      route: string(),
      selfReported: boolean().required(formatMessage(messages.required)),
      standard: boolean(),
      associatedResources: array().of(
        string().required(formatMessage(messages.required))
      ),
    });

  const validationSchema: Schema<
    Partial<FormValues>,
    any,
    any,
    Flags
  > = immunValidationSchema.concat(baseFhirValidationSchema);

  async function handleSubmit(
    values: FormValues,
    { validateForm }: FormikHelpers<FormValues>
  ) {
    if (!patient_id) return;
    values = stripEmptyStrings(values);
    await validateForm(stripEmptyStrings(values));

    let immunization: ImmunizationRecord | null | undefined;
    try {
      const location: LocationModelInput | null =
        values.city || values.country || values.facility || values.region
          ? {
              name: values.facility,
              ...((values.city || values.country || values.region) && {
                address: {
                  city: values.city,
                  country: values.country,
                  state: values.region,
                },
              }),
            }
          : null;

      if (data?.id) {
        const { data: updatedImmun } = await updateImmunization({
          input: {
            immunization_id: data.id,
            immunization: data.getModelInput(values),
            location,
          },
        });
        immunization = updatedImmun;
      } else {
        const { data } = await createImmunization({
          input: {
            immunization: getImmunizationTemplate(patient_id, values),
            location,
          },
        });
        immunization = data;
      }
    } catch (e) {
      enqueueSnackbar({
        variant: 'error',
        message: formatMessage(messages.error),
      });
    } finally {
      await handleRecordFilesSourcePublishing({
        resource: immunization?.getResourceId(),
        files: values.files,
        toPublish: values.toPublish,
        recordSource: getRecordSourceForSubmit({
          isCurator,
          isCreating,
          initialRecordSource: initialValues.recordSource,
          currentRecordSource: values.recordSource,
        }),
      });
      // Existing behavior preserved by default.
      // In auto-curation flow, disableNavigateOnCreate=true will skip this.
      if (immunization && isCreating && !disableNavigateOnCreate) {
        navigate(`${IMMUNIZATIONS}${immunization.id}`);
      }
      onSubmit?.();
    }
  }

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
    >
      {({ setFieldValue, values }) => (
        <Form>
          <DrilldownWrapper
            associatedRecords={data?.associatedRecords}
            backRoute={IMMUNIZATIONS}
            items={[
              {
                editingComponent: (
                  <FormikTextField
                    isHeaderItem
                    name="practitioner"
                    placeholder={formatMessage(messages.practitioner)}
                  />
                ),
                title: data?.practitioner ?? 'Unknown',
                iconName: 'user-doctor',
              },
              {
                editingComponent: (
                  <>
                    <FormikTextField
                      isHeaderItem
                      name="facility"
                      placeholder={formatMessage(messages.facility)}
                    />
                    <FormikTextField
                      isHeaderItem
                      name="city"
                      placeholder={formatMessage(messages.city)}
                    />
                    <FormikTextField
                      isHeaderItem
                      name="region"
                      placeholder={formatMessage(messages.region)}
                    />
                    <FormikTextField
                      isHeaderItem
                      name="country"
                      placeholder={formatMessage(messages.country)}
                    />
                  </>
                ),
                title: data?.facility,
                iconName: 'location-dot',
              },
            ]}
            loading={loading}
            notes={data?.recordNotes}
            resource={data}
            selfReported={Boolean(data?.selfReported)}
            subtitle={
              isEditing || isCreating ? (
                <Stack
                  direction={isMobile ? 'column' : 'row'}
                  columnGap={isMobile ? 0 : 2}
                >
                  <FormikFhirDateField
                    formats={['date', 'year']}
                    label={formatMessage(messages.administrationDate)}
                    name="immunizationDate"
                    onChange={(value) => {
                      setFieldValue('immunizationDate', value);
                    }}
                    required
                    value={data?.immunizationDate}
                    max={true}
                  />
                  <FormikFhirDateField
                    formats={['date', 'year']}
                    label={formatMessage(messages.expirationDate)}
                    name="expirationDate"
                    onChange={(value) => {
                      setFieldValue('expirationDate', value);
                    }}
                    value={data?.expirationDate}
                  />
                </Stack>
              ) : (
                [
                  formatDateLabel(
                    messages.administrationDate,
                    data?.getImmunizationDateStr()
                  ),
                  formatDateLabel(
                    messages.expirationDate,
                    data?.getExpiryDateStr()
                  ),
                ]
              )
            }
            title={data?.vaccineName || formatMessage(messages.title)}
            titleInput={
              <Autocomplete
                disableClearable
                freeSolo
                onChange={(_event, value) => {
                  setFieldValue('title', value);
                }}
                options={VACCINE_CODE_OPTIONS}
                renderInput={(params) => (
                  <Field name="title">
                    {({ field, meta }: FieldProps<string>) => (
                      <TextField
                        {...params}
                        error={meta.touched && Boolean(meta.error)}
                        helperText={
                          meta.touched && meta.error ? meta.error : ''
                        }
                        margin="dense"
                        placeholder={formatMessage(messages.vaccine)}
                        {...field}
                        onChange={(event) =>
                          setFieldValue('title', event.target.value)
                        }
                      />
                    )}
                  </Field>
                )}
                style={{
                  width: 500,
                }}
                value={values.title}
              />
            }
          >
            <ColumnLayout>
              <DrilldownItem
                label={formatMessage(messages.brand)}
                name="brand"
                value={data?.brand}
              />
              <DrilldownItem
                label={formatMessage(messages.route)}
                name="route"
                value={data?.route}
              />
            </ColumnLayout>

            <ColumnLayout>
              <DrilldownItem
                label={formatMessage(messages.dose)}
                name="dose"
                value={data?.dose}
              />
              <DrilldownItem
                label={formatMessage(messages.lotNumber)}
                name="lotNumber"
                value={data?.lotNumber}
              />
            </ColumnLayout>

            <DrilldownItem
              headerLabel={formatMessage(messages.immunizationType)}
              label={
                isCreating || isEditing
                  ? formatMessage(messages.type, {
                      type: values.standard ? 'Standard' : 'Travel',
                    })
                  : formatMessage(messages.immunizationType)
              }
              name="standard"
              type="checkbox"
              value={formatMessage(messages.type, {
                type: data?.standard ? 'Standard' : 'Travel',
              })}
            />

            <RelatedRecordsForm associations={values.associatedResources} />

            <RecordAttachmentForm
              isCreating={isCreating}
              onFileChange={(files: DroppedFile[]) => {
                setFieldValue('files', files);
              }}
              patientId={patient_id}
              record={{
                attachments: data?.attachments,
                resourceId: data?.getResourceId(),
              }}
            />

            <HorizontalRule />
            <RecordSubmitButton />
          </DrilldownWrapper>
        </Form>
      )}
    </Formik>
  );
};

export default ImmunizationDrilldown;
